import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";
import { generateQuiz } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import { Textarea } from "~/atoms/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";

interface Props {
  documentVersionId: number;
  onCreated: () => void;
}

/**
 * Generate and persist a single quiz for one document version. The optional
 * prompt narrows the generated questions to a focus area.
 */
export const CreateQuizForVersion = ({
  documentVersionId,
  onCreated,
}: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleOpenChange = (next: boolean) => {
    if (next) setPrompt("");
    setOpen(next);
  };

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      await generateQuiz(documentVersionId, prompt.trim() || undefined);
      onCreated();
      setOpen(false);
    } catch {
      toast.error(t(($) => $.anchoring.manage.error));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>{t(($) => $.anchoring.manage.create)}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(($) => $.anchoring.manage.create)}</DialogTitle>
          <DialogDescription>
            {t(($) => $.anchoring.manage.createDescription)}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          placeholder={t(($) => $.anchoring.create.promptPlaceholder)}
          disabled={isCreating}
        />
        <DialogFooter>
          <Button onClick={handleCreate} disabled={isCreating}>
            {isCreating && <Loader2 className="size-4 animate-spin" />}
            {isCreating
              ? t(($) => $.anchoring.manage.creating)
              : t(($) => $.anchoring.manage.create)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
